import { BannerInlinePlaceholder } from '@tekface/smart-activation';
import {
  Body,
  Breadcrumbs,
  Button,
  Col,
  Container,
  Flex as DSFlex,
  Divider,
  styled,
} from '@teko/ds-desktop';
import { BackOutlined } from '@teko/ds-icon';
import {
  BlockHeader,
  FacebookComment,
  FlashSaleBar,
  NoProductsFound,
  ProductCarousel,
} from '@teko/teko-ecommerce';
import { trackViewProductDetail } from '@teko/tracking';
import { DividerStyleEnum } from '@teko/types/shared';
import { LazyComponent } from 'components/LazyComponent';
import {
  Card,
  Content,
  DescriptionCol,
  NotFoundProductLayout,
  ProductVariantsWrapper,
  Wrapper,
} from 'components/ProductDetail/styles';
import { RelateVideosAndPosts } from 'components/RelatedVideosAndPosts';
import { WishlistButton } from 'components/WishlistButton';
import { RECENTLY_VIEWED_PRODUCTS_KEY } from 'constants/index';
import { INLINE_BANNER_CLASSNAME } from 'constants/smartBanner';
import {
  useFeatureFlags,
  useProductDetailState,
  useTenantConfig,
} from 'contexts';
import DOMPurify from 'dompurify';
import {
  getBaseDomain,
  getEnv,
  isServerless,
  saveRecentlyProduct,
} from 'helpers';
import { useIdentifiedPage } from 'hooks';
import { t } from 'i18n';
import { BreadcrumbSchema } from 'modules/product-detail/shared/BreadcrumbSchema';
import { ReviewSection } from 'modules/product-detail/shared/ReviewSection';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useEffect, useRef } from 'react';
import { Box, Flex } from 'reflexbox';
import { ProductDetailPromotion } from 'types/promotions';
import { MetaData } from '../../shared/MetaData';
import { ShareButtons } from './ShareButtons';

const Policies = dynamic(() =>
  import('components/Policies').then((mod) => mod.Policies),
);
const ProductRelatedPromotions = dynamic(() =>
  import('./ProductRelatedPromotions').then(
    (mod) => mod.ProductRelatedPromotions,
  ),
);

const ProductCombo = dynamic(() =>
  import('./ProductCombo').then((mod) => mod.ProductCombo),
);
const ProductHeader = dynamic(() =>
  import('./ProductHeader').then((mod) => mod.ProductHeader),
);
const ActionButtons = dynamic(() =>
  import('./ActionButtons').then((mod) => mod.ActionButtons),
);
const ProductImages = dynamic(() =>
  import('./ProductImages').then((mod) => mod.ProductImages),
);
const ProductVariants = dynamic(() =>
  import('../../shared/ProductVariants').then((mod) => mod.ProductVariants),
);
const PriceAtLocationPicker = dynamic(() =>
  import('../../shared/PriceAtLocationPicker').then(
    (mod) => mod.PriceAtLocationPicker,
  ),
);
const ProductSpecifications = dynamic(() =>
  import('./ProductSpecifications').then((mod) => mod.ProductSpecifications),
);
const Description = dynamic(() =>
  import('./ProductDescription').then((mod) => mod.ProductDescription),
);
const ProductPriceAndPromotion = dynamic(() =>
  import('./ProductPriceAndPromotion').then(
    (mod) => mod.ProductPriceAndPromotion,
  ),
);
const ConfirmOver18YearOldModal = dynamic(() =>
  import('../../shared/ConfirmOver18YearOldModal').then(
    (mod) => mod.ConfirmOver18YearOldModal,
  ),
);

const FAQSection = dynamic(() =>
  import('components/FAQ').then((mod) => mod.FAQSection),
);

const NewReviewSection = dynamic(() =>
  import('../../shared/ProductReview/ReviewSection').then(
    (mod) => mod.ReviewSection,
  ),
);

interface DetailPageProps {
  showRelatedPromotion?: boolean;
  extraPromotions?: ProductDetailPromotion[];
}
const renderFlashSaleBadge = (
  total: number,
  sold: number,
  stock: number,
  endAt: string,
) => {
  const remain = Math.min(total - sold, stock);
  const message =
    sold / total > 0.9 || remain < 5
      ? t('Sắp cháy hàng')
      : t('Còn lại {{remain}}', { remain: remain });
  return (
    <FlashSaleBar
      endAt={new Date(endAt)}
      text={message}
      margin={['s16', 's0']}
      padding="s8"
    />
  );
};

export const DetailPage: React.FC<DetailPageProps> = ({
  showRelatedPromotion = true,
  extraPromotions = [],
}: DetailPageProps) => {
  const { flags } = useFeatureFlags();
  const specificationRef = useRef<HTMLDivElement | null>(null);
  const {
    product,
    stockAvailable,
    showFlashSale,
    primitiveProduct,
    relatedProducts,
    showPopupCheckAge,
    setShowPopupCheckAge,
    breadcrumbs,
    inlineBanners,
  } = useProductDetailState();
  const {
    allowCommentAndReceiveReply,
    currentLanguage,
    showDetailPageGoBackArrow,
    epicOmni1550,
  } = useTenantConfig();
  const { back } = useRouter();

  const flashSaleBadge =
    showFlashSale && product?.flashSale && stockAvailable
      ? renderFlashSaleBadge(
          product.flashSale.total,
          product.flashSale.used,
          stockAvailable,
          product.flashSale.endAt,
        )
      : null;
  useIdentifiedPage('productDetail');
  useEffect(() => {
    if (product) {
      saveRecentlyProduct(product.sku, RECENTLY_VIEWED_PRODUCTS_KEY);
      trackViewProductDetail({
        price: product.price?.price,
        name: product.name,
        sku: product.sku,
        brand: product.brand?.name,
        category: product.categories?.[0]?.name || '',
        sellerSku: product?.sellerSku,
      });
    }
  }, [product?.sku]);

  const scrollToSpecification = () => {
    specificationRef.current?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  if (!product) {
    return (
      <NotFoundProductLayout>
        <NoProductsFound show text={t('Không tìm thấy sản phẩm')} />
      </NotFoundProductLayout>
    );
  }

  return (
    <>
      <BreadcrumbSchema />
      <MetaData />
      <Container>
        <Wrapper>
          <Breadcrumbs breadcrumbs={breadcrumbs || []} />
          <Flex marginBottom={24}>
            <Box width="75.6%" marginRight={16}>
              <Card style={{ padding: '24px', height: '100%' }}>
                <Flex>
                  <Box width="40.5%" paddingRight={16}>
                    <ProductImages />
                    <Divider
                      dividerStyle={DividerStyleEnum.dashed}
                      margin={['s12', 's0']}
                      color="divider"
                    />
                    <Content
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify?.sanitize?.(
                          product?.seoInfo?.shortDescription || '',
                        ),
                      }}
                    />
                    <Button variant="link" onClick={scrollToSpecification}>
                      {t('Xem thông tin chi tiết')}
                    </Button>
                    <Flex>
                      <ShareButtons />
                      <Flex marginTop={12} marginLeft={12} alignItems="center">
                        <Body level={1}>{t('Yêu thích ')}</Body>
                        <WishlistButton sku={product.sku} />
                      </Flex>
                    </Flex>
                  </Box>
                  <Box width="59.5%">
                    {flashSaleBadge}
                    <ProductHeader />
                    <ProductVariantsWrapper>
                      <ProductVariants />
                    </ProductVariantsWrapper>
                    {/* Location Picker */}
                    {flags?.epic_ecom_284 && <PriceAtLocationPicker />}
                    <ProductPriceAndPromotion />
                    <ActionButtons />
                    {/* smart banner */}
                    <BannerInlinePlaceholder
                      banners={inlineBanners}
                      className={
                        INLINE_BANNER_CLASSNAME.INLINE_PDP_RELATEDPROMO
                      }
                    />
                    {showRelatedPromotion && (
                      <ProductRelatedPromotions
                        extraPromotions={extraPromotions}
                      />
                    )}
                    <div className="productAfterPromoBox" />
                  </Box>
                </Flex>
              </Card>
            </Box>
            <Box width="24.4%">
              <Policies wrapperProps={{ itemVerticalSpacing: 's16' }} />
              <BannerInlinePlaceholder
                banners={inlineBanners}
                className={INLINE_BANNER_CLASSNAME.INLINE_PDP_SERVICE}
              />
            </Box>
          </Flex>
          <Flex marginBottom={24}>
            <Box width="75.6%" marginRight={16}>
              <ProductCombo />
            </Box>
          </Flex>
          <LazyComponent>
            <DSFlex marginBottom="s24" gap="s16">
              <DescriptionCol height="100%" width="calc((100% - 16px)/3 * 2)">
                <Description />
              </DescriptionCol>
              <Col
                ref={specificationRef}
                width="calc((100% - 16px)/3)"
                height="100%"
                style={{ scrollMarginTop: '137px' }}
              >
                <ProductSpecifications />
                {(!!product.seoInfo?.relatedVideos ||
                  !!product.seoInfo?.relatedPosts) && (
                  <RelateVideosAndPosts
                    relatedVideos={product.seoInfo?.relatedVideos}
                    relatedPosts={product.seoInfo?.relatedPosts}
                  />
                )}
              </Col>
            </DSFlex>
            {epicOmni1550 ? (
              <NewReviewSection />
            ) : (
              <ReviewSection product={product} />
            )}
          </LazyComponent>
          {!!product.seoInfo?.faq && product.seoInfo.faq.length > 0 ? (
            <FAQSection
              faq={product.seoInfo?.faq || []}
              style={{ padding: '0px', width: '100%' }}
            />
          ) : null}
          <LazyComponent>
            {relatedProducts.length ? (
              <Card style={{ marginBottom: '24px' }}>
                <>
                  <BlockHeader title={t('Sản phẩm liên quan')} />
                  <ProductCarousel
                    products={relatedProducts}
                    productsPerRow={5}
                    regionName="relatedProducts"
                    virtual={isServerless()}
                  />
                </>
              </Card>
            ) : null}
          </LazyComponent>
          {allowCommentAndReceiveReply && (
            <LazyComponent>
              <ProductCommentWrapper>
                <FacebookComment
                  appId={getEnv('NEXT_PUBLIC_FACEBOOK_APP_ID') as string}
                  language={currentLanguage}
                  href={`${getBaseDomain()}/${
                    primitiveProduct?.productInfo?.canonical
                  }`}
                />
              </ProductCommentWrapper>
            </LazyComponent>
          )}
        </Wrapper>
      </Container>
      {showPopupCheckAge !== undefined && (
        <ConfirmOver18YearOldModal
          showPopupCheckAge={showPopupCheckAge}
          setShowPopupCheckAge={setShowPopupCheckAge}
        />
      )}
      {!!showDetailPageGoBackArrow && isServerless() && (
        <IconWrapper>
          <BackOutlined
            size={30}
            color="#333"
            onClick={() => {
              back();
            }}
          />
        </IconWrapper>
      )}
    </>
  );
};

const ProductCommentWrapper = styled.div`
  background-color: #fff;
  margin-top: 1rem;
  margin-bottom: 1rem;
`;

const IconWrapper = styled.div`
  position: fixed;
  top: 50%;
  left: 8px;
  right: auto;
  z-index: 1030;
  cursor: pointer;
  display: flex;
  -webkit-box-align: center;
  align-items: center;
  -webkit-box-pack: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  transform: translateY(-50%);
  background-color: rgba(168, 168, 168, 0.46);
  border-top-right-radius: 100px;
  border-bottom-right-radius: 100px;
`;
